import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  TablePagination,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  LinearProgress,
  Divider,
} from "@mui/material";
import {
  Group as ReferralIcon,
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  HourglassEmpty as PendingIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import api from "../../utils/api";

const SolarReferrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    validReferrals: 0,
    pendingReferrals: 0,
    requiredReferrals: 3,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        setLoading(true);
        const response = await api.get("/solar-program/referrals");
        if (response.data.status === "success") {
          setReferrals(response.data.data.referrals || []);
          setStats({
            totalReferrals: response.data.data.totalReferrals || 0,
            validReferrals: response.data.data.validReferrals || 0,
            pendingReferrals: response.data.data.pendingReferrals || 0,
            requiredReferrals: response.data.data.requiredReferrals || 3,
          });
        }
        setError(null);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch referral data"
        );
        // console.error("Solar referrals fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReferrals();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate progress percentage
  const referralProgress = Math.min(
    (stats.validReferrals / stats.requiredReferrals) * 100,
    100
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
        }}
      >
        <ReferralIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h5">Solar Program Referrals</Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Your Progress
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body2">
                        Valid Referrals: {stats.validReferrals}/
                        {stats.requiredReferrals}
                      </Typography>
                      <Typography variant="body2">
                        {Math.round(referralProgress)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={referralProgress}
                      sx={{ height: 8, borderRadius: 5, my: 1 }}
                    />

                    <Alert
                      severity={
                        stats.validReferrals >= stats.requiredReferrals
                          ? "success"
                          : "info"
                      }
                      sx={{ mt: 2 }}
                    >
                      <AlertTitle>
                        {stats.validReferrals >= stats.requiredReferrals
                          ? "Congratulations!"
                          : "Keep Going!"}
                      </AlertTitle>
                      {stats.validReferrals >= stats.requiredReferrals
                        ? "You've met the required number of referrals for the solar program."
                        : `You need ${
                            stats.requiredReferrals - stats.validReferrals
                          } more valid referral(s) to qualify for the solar program.`}
                    </Alert>
                  </Box>

                  <Box sx={{ mt: 3, mb: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "background.default",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="h4" color="primary">
                            {stats.totalReferrals}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Referrals
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "background.default",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="h4" color="success.main">
                            {stats.validReferrals}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Valid Referrals
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: "background.default",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="h4" color="warning.main">
                            {stats.pendingReferrals}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pending Referrals
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Referrals List
            </Typography>

            {referrals.length === 0 ? (
              <Alert severity="info">
                You haven't referred anyone yet. Share your referral link to
                start earning rewards!
              </Alert>
            ) : (
              <Card>
                <CardContent sx={{ p: 0 }}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Joined Date</TableCell>
                          <TableCell>Deposit Status</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referrals
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((referral) => (
                            <TableRow key={referral._id}>
                              <TableCell>{referral.name}</TableCell>
                              <TableCell>{referral.email}</TableCell>
                              <TableCell>
                                {format(
                                  new Date(referral.createdAt),
                                  "MMM dd, yyyy"
                                )}
                              </TableCell>
                              <TableCell>
                                {referral.hasDeposited ? (
                                  <Chip
                                    icon={<ValidIcon />}
                                    label="Deposited"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={<PendingIcon />}
                                    label="No Deposit"
                                    color="warning"
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {referral.isValid ? (
                                  <Chip
                                    icon={<ValidIcon />}
                                    label="Valid"
                                    color="success"
                                    size="small"
                                  />
                                ) : (
                                  <Chip
                                    icon={<InvalidIcon />}
                                    label="Invalid"
                                    color="error"
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={referrals.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                  />
                </CardContent>
              </Card>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mt: 3 }}>
            <Alert severity="info" icon={false}>
              <Typography variant="subtitle2">
                How Solar Referrals Work:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                <li>
                  <Typography variant="body2">
                    <strong>Valid Referrals</strong>: Only users who have made a
                    deposit and had it approved count as valid referrals.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Pending Referrals</strong>: These are users who
                    signed up with your referral code but haven't made a deposit
                    yet.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Qualification</strong>: You need{" "}
                    {stats.requiredReferrals} valid referrals to qualify for
                    your free solar system.
                  </Typography>
                </li>
              </Box>
            </Alert>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SolarReferrals;
